import React, { useMemo, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Divider,
    IconButton,
    Tooltip,
    Button,
    TextField,
    InputAdornment,
    Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    getFailureContext,
    getDisplaySteps,
    isStepFailed,
    isStepPassed,
    formatLogsForDisplay,
} from "../../../utils/testCaseFailureUtils";

const FailureExecutionDetail = ({ testCase }) => {
    const displaySteps = getDisplaySteps(testCase);

    if (displaySteps.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                No execution details available for this failure.
            </Typography>
        );
    }

    return (
        <Box sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #f1f5f9',
            bgcolor: '#f8fafc'
        }}>
            {displaySteps.map((step, sIdx) => {
                const failed = isStepFailed(step.status);
                const passed = isStepPassed(step.status);
                const logsText = formatLogsForDisplay(step.logs);

                return (
                    <Box key={sIdx}>
                        <Box sx={{
                            p: 1.5,
                            bgcolor: failed ? '#fff1f2' : 'transparent',
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center'
                        }}>
                            <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: passed ? '#22c55e' : failed ? '#ef4444' : '#94a3b8',
                                flexShrink: 0
                            }} />
                            <Typography variant="body2" sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: failed ? 700 : 500,
                                color: failed ? '#991b1b' : passed ? '#1e293b' : '#64748b'
                            }}>
                                {step.name}
                            </Typography>
                        </Box>
                        {logsText && (
                            <Box sx={{ px: 2, pb: failed && step.errorMessage ? 0 : 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5 }}>
                                    Logs
                                </Typography>
                                <Box sx={{
                                    p: 2,
                                    bgcolor: '#eff6ff',
                                    borderRadius: 2,
                                    border: '1px solid #bfdbfe'
                                }}>
                                    <Typography variant="caption" component="pre" sx={{
                                        color: '#1e40af',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        fontSize: '0.75rem',
                                        lineHeight: 1.6,
                                        m: 0
                                    }}>
                                        {logsText}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {failed && step.errorMessage && (
                            <Box sx={{ px: 2, pb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.5 }}>
                                    Error
                                </Typography>
                                <Box sx={{
                                    p: 2,
                                    bgcolor: '#1e293b',
                                    borderRadius: 2,
                                    border: '1px solid #334155'
                                }}>
                                    <Typography variant="caption" component="pre" sx={{
                                        color: '#fb7185',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        fontSize: '0.75rem',
                                        lineHeight: 1.6,
                                        m: 0
                                    }}>
                                        {step.errorMessage}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

const ReportAnalysisContent = ({ testCases, iteration }) => {
    const [expandedCause, setExpandedCause] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const analysisData = useMemo(() => {
        if (!testCases || testCases.length === 0) return null;

        const failureCauses = {};
        const failedSteps = {};
        const failedScenarios = [];

        testCases.forEach((tc) => {
            if (tc.result === "Fail") {
                const { failedStepName, shortMsg, fullError, logs } = getFailureContext(tc);

                if (!failedSteps[failedStepName]) {
                    failedSteps[failedStepName] = { count: 0, firstCause: shortMsg };
                }
                failedSteps[failedStepName].count += 1;

                if (!failureCauses[shortMsg]) {
                    failureCauses[shortMsg] = {
                        count: 0,
                        examples: [],
                        stepName: failedStepName
                    };
                }
                failureCauses[shortMsg].count += 1;
                failureCauses[shortMsg].examples.push(tc);

                failedScenarios.push({
                    id: tc.testCaseId,
                    summary: tc.testCaseSummary,
                    failedStep: failedStepName,
                    errorMessage: fullError,
                    logs,
                    screenshot: tc.screenshot,
                    video: tc.video,
                    causeMsg: shortMsg,
                    original: tc
                });
            }
        });

        return {
            failureCauses: Object.entries(failureCauses)
                .sort((a, b) => b[1].count - a[1].count)
                .map(([msg, data]) => ({ msg, ...data })),
            failedSteps: Object.entries(failedSteps)
                .sort((a, b) => b[1].count - a[1].count)
                .map(([step, data]) => ({ step, ...data })),
            failedScenarios: failedScenarios.sort((a, b) => a.id.localeCompare(b.id))
        };
    }, [testCases]);

    const filteredScenarios = useMemo(() => {
        if (!analysisData) return [];
        if (!searchQuery) return analysisData.failedScenarios;
        const q = searchQuery.toLowerCase();
        return analysisData.failedScenarios.filter(s =>
            s.summary.toLowerCase().includes(q) ||
            s.failedStep.toLowerCase().includes(q) ||
            s.id.toLowerCase().includes(q) ||
            s.errorMessage.toLowerCase().includes(q)
        );
    }, [analysisData, searchQuery]);

    if (!analysisData || analysisData.failedScenarios.length === 0) {
        return (
            <Box sx={{ 
                p: 8, 
                textAlign: "center", 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f0fdf4',
                borderRadius: 4,
                border: '1px solid #bbf7d0',
                mt: 2
            }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#16a34a', mb: 2 }} />
                <Typography variant="h3" sx={{ color: '#15803d', fontWeight: 700, mb: 1 }}>
                    Great Job!
                </Typography>
                <Typography variant="h5" sx={{ color: '#16a34a', fontWeight: 500 }}>
                    No failures observed in this iteration.
                </Typography>
            </Box>
        );
    }

    const scrollToDetail = (causeMsg) => {
        const idx = analysisData.failureCauses.findIndex(c => c.msg === causeMsg);
        const element = document.getElementById(`cause-${idx}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setExpandedCause(idx);
        }
    };

    return (
        <Box sx={{ pb: 4, bgcolor: "#f8fafc", minHeight: '100vh', p: 3, borderRadius: 3 }}>
            {/* Simple Header - Primary Branding */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ color: '#64748b', fontWeight: 500 }}>
                    Intelligent breakdown of automation execution gaps
                </Typography>
            </Box>

            {/* Heatmap Section - 60-20-20 Division using Paper */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                    bgcolor: '#fff',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        Failure Point Heatmap
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Frequency breakdown by failed step or test name when steps are unavailable
                    </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 350 }}>
                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', width: '60%', py: 1.5, bgcolor: '#f8fafc' }}>Failed Step Definition</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', width: '20%', bgcolor: '#f8fafc' }}>Frequency</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', width: '20%', bgcolor: '#f8fafc' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {analysisData.failedSteps.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                        <Typography color="textSecondary">No failure points recorded.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : analysisData.failedSteps.map((item, index) => (
                                <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ py: 1.5 }}>
                                        <Tooltip title={item.step} placement="top-start">
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#1e293b',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: 'block'
                                                }}
                                            >
                                                {item.step}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={`${item.count} total occurrences`}>
                                            <Chip
                                                label={item.count}
                                                variant="outlined"
                                                sx={{
                                                    borderColor: '#fbbf24',
                                                    color: '#b45309',
                                                    fontWeight: 700,
                                                    borderRadius: 2,
                                                    height: 24
                                                }}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Root Cause">
                                            <IconButton size="small" onClick={() => scrollToDetail(item.firstCause)} sx={{ color: '#6366f1' }}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Visual Spacer between blocks */}
            <Box sx={{ height: 60 }} />

            {/* Consolidated Failure Log Table - 60-30-10 Division with Ellipsis */}
            <Paper
                elevation={0}
                sx={{
                    p: 4, // More internal padding for a premium look
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                    bgcolor: '#fff',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)'
                }}
            >
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            Consolidated Failure Log
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Unified view of all failed test scenarios
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        placeholder="Search failure log..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: '8px',
                                bgcolor: '#f8fafc',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            }
                        }}
                    />
                </Box>
                <TableContainer sx={{ maxHeight: 450, overflow: 'auto' }}>
                    <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} aria-label="failure master table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#1e40af', width: '60%', py: 1.5, bgcolor: '#eff6ff' }}>Testcase ID & Scenario</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#1e40af', width: '30%', bgcolor: '#eff6ff' }}>Failure Step</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700, color: '#1e40af', width: '10%', bgcolor: '#eff6ff' }}>Evidence</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredScenarios.length > 0 ? filteredScenarios.map((row, index) => (
                                <TableRow key={index} hover>
                                    <TableCell sx={{ py: 2 }}>
                                        <Box sx={{ overflow: 'hidden' }}>
                                            <Chip
                                                label={row.id}
                                                size="small"
                                                sx={{ mb: 0.5, bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700, borderRadius: 1.5 }}
                                            />
                                            <Tooltip title={row.summary} placement="top-start">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#1e293b',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {row.summary}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={row.errorMessage || row.failedStep} placement="top">
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    color: '#e11d48',
                                                    fontWeight: 700,
                                                    fontFamily: 'monospace',
                                                    bgcolor: '#fff1f2',
                                                    p: 0.5,
                                                    borderRadius: 1,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {row.failedStep}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={0.5} justifyContent="center">
                                            {row.screenshot && (
                                                <Tooltip title="View Evidence Screenshot">
                                                    <IconButton size="small" component="a" href={row.screenshot} target="_blank" sx={{ color: '#059669' }}>
                                                        <ImageIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {row.video && (
                                                <Tooltip title="View Failure Video">
                                                    <IconButton size="small" component="a" href={row.video} target="_blank" sx={{ color: '#7c3aed' }}>
                                                        <VideocamIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Jump to Detailed Analysis">
                                                <IconButton size="small" onClick={() => scrollToDetail(row.causeMsg)} sx={{ color: '#3b82f6' }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                        <Typography color="textSecondary">No matching failures found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Detailed Breakdown Section */}
            <Typography variant="h4" sx={{ mt: 6, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, color: '#1e293b', fontWeight: 700 }}>
                <Box sx={{ bgcolor: '#e11d48', width: 4, height: 24, borderRadius: 1 }} />
                Root Cause Breakdown & Reconstruction
            </Typography>

            {analysisData.failureCauses.map((cause, idx) => (
                <Accordion
                    key={idx}
                    id={`cause-${idx}`}
                    expanded={expandedCause === idx}
                    onChange={() => setExpandedCause(expandedCause === idx ? null : idx)}
                    sx={{
                        mb: 2,
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px !important',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { border: '1px solid #94a3b8', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            bgcolor: expandedCause === idx ? '#f8fafc' : '#fff',
                            transition: 'all 0.2s ease',
                            borderBottom: expandedCause === idx ? '1px solid #e2e8f0' : 'none'
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                            <Chip
                                label={`${cause.count} Impacted`}
                                sx={{
                                    bgcolor: '#fff1f2',
                                    color: '#e11d48',
                                    fontWeight: 800,
                                    fontSize: '0.7rem',
                                    height: 22
                                }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                                {cause.msg}
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: '#fff', p: 4 }}>
                        <Stack spacing={4}>
                            {cause.examples.map((ex, eIdx) => (
                                <Box key={eIdx} sx={{ position: 'relative' }}>
                                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                <Chip label={ex.testCaseId} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                                                <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700 }}>
                                                    {ex.testCaseSummary}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            {ex.screenshot && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ImageIcon />}
                                                    size="small"
                                                    href={ex.screenshot}
                                                    target="_blank"
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                        boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)',
                                                        borderRadius: 2,
                                                        '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }
                                                    }}
                                                >
                                                    Screenshot
                                                </Button>
                                            )}
                                            {ex.video && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<VideocamIcon />}
                                                    size="small"
                                                    href={ex.video}
                                                    target="_blank"
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                                        boxShadow: '0 4px 10px rgba(124, 58, 237, 0.2)',
                                                        borderRadius: 2,
                                                        '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }
                                                    }}
                                                >
                                                    Video
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>

                                    <FailureExecutionDetail testCase={ex} />
                                    {eIdx < cause.examples.length - 1 && <Divider sx={{ mt: 4, mb: 1, borderStyle: 'dashed' }} />}
                                </Box>
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default ReportAnalysisContent;
